import { type PropsWithChildren, useState } from 'react';

import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import useLocalSettings from '@/stores/useLocalSettings';

import { Button } from '../shadcn/button';

import AmountCircle from './AmountCircle';

type Props = PropsWithChildren<{
  icon: LucideIcon;
  title: string;
  noItemsMessage?: string;
  more?: string;
  totalItems?: number;
  relevantItems?: number;
  isLoading?: boolean;
  defaultCollapsed?: boolean;
}>;

export default function PartyTabSection({
  icon,
  title,
  noItemsMessage,
  more,
  relevantItems,
  totalItems,
  isLoading,
  defaultCollapsed,
  children,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(!defaultCollapsed);
  const { onlyShowRelevant } = useLocalSettings();

  const Icon = icon;

  if (onlyShowRelevant && relevantItems !== undefined && relevantItems === 0)
    return null;

  const amount = onlyShowRelevant ? relevantItems : totalItems;

  const showNoItemsMessage =
    totalItems !== undefined &&
    totalItems === 0 &&
    !onlyShowRelevant &&
    !isLoading;

  return (
    <div className="flex flex-col border-b">
      <div
        className="flex cursor-pointer items-center justify-between gap-2 bg-neutral-100 p-2 dark:bg-neutral-900"
        onClick={() => setIsOpen((isOpen) => !isOpen)}
      >
        <div className="flex w-full items-center gap-2">
          <div className="flex w-full items-center gap-2 text-sm select-none">
            <Icon className="text-primary-500 size-4 shrink-0" />

            {title}
            {amount !== undefined && <AmountCircle amount={amount} />}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-xs"
            className="hover:bg-primary-100 dark:hover:bg-primary-700/50"
          >
            {isOpen && <ChevronDown className="size-4" />}
            {!isOpen && <ChevronUp className="size-4" />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-2 p-2">
          {isLoading !== undefined && isLoading && (
            <div className="flex h-16 items-center justify-center">
              <Loader2 className="text-primary-500 size-6 animate-spin" />
            </div>
          )}

          {!showNoItemsMessage && (
            <div className="flex flex-col gap-2">{children}</div>
          )}

          {showNoItemsMessage && (
            <div className="text-muted-foreground flex items-center justify-center py-2 text-xs">
              {noItemsMessage}
            </div>
          )}

          {more && !showNoItemsMessage && (
            <div className="flex justify-end">
              <Button variant="ghost" size="xs" asChild>
                <Link to={more}>
                  {t('VIEW_ALL')}
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
